import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  LocalShipping,
  Store,
  Person,
  Receipt,
  CheckCircle,
  Print,
  Search,
  Add,
  Delete,
  PrintDisabled,
  Speed,
  Save,
  Home,
  ContentCopy,
  Upload,
  CloudUpload,
  FileDownload,
  Refresh,
  FlightTakeoff
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import api from '../services/api';
import HKReturnLabelPanel from '../components/HKReturnLabelPanel';

interface ShippingOrder {
  orderId: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  storeId: string;
  storeName: string;
  storeAddress?: string;
  logisticsSubType: string;
  goodsAmount: number;
  goodsName: string;
  isCollection: 'Y' | 'N';
  remarks?: string;
  // Home delivery specific fields
  deliveryAddress?: string;
  weight?: number;
  declaredValue?: number;
  deliveryDate?: string;
}

interface HoverZoomImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  borderColor?: string;
}

interface SimpleHoverZoomProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  zoomSize?: number;
  borderColor?: string;
}

interface LogisticsResult {
  orderId: string;
  logisticsId: string;
  paymentNo: string;
  validationNo?: string;
  storeId: string;
  storeName: string;
  logisticsSubType: string;
  // Hsinchu specific fields
  trackingNumber?: string;
  labelImage?: string;
  destinationCode?: string;
  qrCode?: string;
  carrier?: string;
  // SF domestic: label/invoice are hosted PDFs, not a hex image
  labelUrl?: string;
  invoiceUrl?: string;
}

interface HsinchuLabelData {
  trackingNumber?: string;
  recipientName?: string;
  recipientAddress?: string;
  weight?: number;
  [key: string]: any;
}

interface ExcelRow {
  檯帳編號: number;
  檯帳名稱: string;
  門市店號: number;
  門市名稱: string;
  電話: string;
  地址: string;
  配送日期?: string;
}

interface BatchProcessResult {
  orderId: string;
  status: 'success' | 'error' | 'pending';
  trackingNumber?: string;
  error?: string;
}

const HOME_DELIVERY_PRESETS = [
  { label: '康是美北倉DC', address: '桃園市中壢區北園路37號', phone: '0229636269' },
  { label: '康是美南倉DC', address: '高雄市仁武區仁心路310號', phone: '0229636269' },
  { label: '康是美西園倉DC', address: '桃園市中壢區西園路105號', phone: '0229636269' },
  { label: '會計 (允榮記帳士)', name: '允榮記帳士事務所', address: '台北市大同區民權西路106號3樓之1', phone: '0225576828' }
];

const ShippingOrderPage = () => {
  const [mainTab, setMainTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [alert, setAlert] = useState<{ show: boolean; type: 'success' | 'error' | 'warning' | 'info'; message: string }>({
    show: false,
    type: 'info',
    message: ''
  });
  const [selectedStore, setSelectedStore] = useState<any>(null);
  const [orderResults, setOrderResults] = useState<LogisticsResult[]>([]);
  const [batchMode, setBatchMode] = useState(false);
  const [orders, setOrders] = useState<ShippingOrder[]>([]);
  const [currentOrder, setCurrentOrder] = useState<ShippingOrder>({
    orderId: '',
    customerName: '',
    customerPhone: '',
    customerEmail: 'taichiuchiu727@gmail.com',
    storeId: '',
    storeName: '',
    storeAddress: '',
    logisticsSubType: 'UNIMARTC2C',
    goodsAmount: 100,
    goodsName: '',
    isCollection: 'N',
    remarks: '',
    deliveryAddress: '',
    weight: 1,
    declaredValue: 0,
    deliveryDate: ''
  });

  // New state for delivery mode
  const [deliveryMode, setDeliveryMode] = useState<'pickup' | 'home'>('pickup');

  // Which carrier fulfils 宅配到府. Kept separate from deliveryMode so every
  // existing `deliveryMode === 'home'` branch keeps working unchanged and only
  // the carrier-specific bits (API call, label printing, labels) fork.
  const [homeCarrier, setHomeCarrier] = useState<'hsinchu' | 'sf'>('hsinchu');
  const isSf = deliveryMode === 'home' && homeCarrier === 'sf';
  const carrierName = isSf ? '順豐速運' : '新竹物流';
  // 順豐 is still on the international product code until SF issues the 台灣宅配
  // one — labels bill off the export tariff. Warn rather than hide it.
  const [sfUsingIntlProduct, setSfUsingIntlProduct] = useState(false);

  // Excel upload states
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelData, setExcelData] = useState<ExcelRow[]>([]);
  const [excelPreview, setExcelPreview] = useState<ExcelRow[]>([]);
  const [batchProcessResults, setBatchProcessResults] = useState<BatchProcessResult[]>([]);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  // New state for repeating orders
  const [repeatCount, setRepeatCount] = useState(1);

  const API_BASE = process.env.REACT_APP_API_BASE || 'https://order-management-system-production.up.railway.app';

  const steps = [
    '設定模式',
    deliveryMode === 'home' ? (batchMode ? '上傳Excel' : '宅配資訊') : (batchMode ? '批次訂單' : '訂單資訊'),
    deliveryMode === 'home' ? '確認訂單' : '物流方式',
    '處理訂單'
  ];

  const logisticsOptions = [
    { value: 'UNIMARTC2C', label: '7-ELEVEN 超商取貨', icon: '🏪' },
    { value: 'FAMIC2C', label: '全家超商取貨', icon: '🏬' },
    { value: 'HILIFEC2C', label: 'Hi-Life 萊爾富取貨', icon: '🏪' },
    { value: 'OKMARTC2C', label: 'OK超商取貨', icon: '🏪' },
    { value: 'HSINCHU_HOME', label: '新竹物流宅配', icon: '🚚' },
    { value: 'SF_HOME', label: '順豐宅配', icon: '📦' }
  ];

  // Utility functions
  const showAlert = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'info', message: '' }), 5000);
  };

  const generateOrderId = (): string => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `ORD${timestamp}${random}`;
  };

  // Excel file handling
  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setExcelFile(file);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData: ExcelRow[] = XLSX.utils.sheet_to_json(worksheet);

        setExcelData(jsonData);
        setExcelPreview(jsonData.slice(0, 10)); // Preview first 10 rows

        showAlert('success', `成功載入 ${jsonData.length} 筆資料`);
      } catch (error) {
        console.error('Error reading Excel file:', error);
        showAlert('error', 'Excel 檔案讀取失敗');
      }
    };

    reader.readAsBinaryString(file);
  };

  // Convert Excel data to shipping orders
  const convertExcelToOrders = (): ShippingOrder[] => {
    return excelData.map((row) => {
      // Parse and format delivery date if present
      let formattedDeliveryDate = '';
      if (row.配送日期) {
        try {
          // Handle Excel date formats (could be string or Excel serial date)
          let dateValue = row.配送日期;

          // If it's an Excel serial number (number of days since 1900-01-01)
          if (typeof dateValue === 'number') {
            const excelEpoch = new Date(1900, 0, 1);
            const days = dateValue - 2; // Excel has a leap year bug, subtract 2
            const parsedDate = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
            formattedDeliveryDate = parsedDate.toISOString().split('T')[0];
          }
          // If it's already a string date
          else if (typeof dateValue === 'string') {
            const parsedDate = new Date(dateValue);
            if (!isNaN(parsedDate.getTime())) {
              formattedDeliveryDate = parsedDate.toISOString().split('T')[0];
            }
          }
        } catch (error) {
          console.warn('Failed to parse delivery date for row:', row, error);
        }
      }

      return {
        orderId: generateOrderId(),
        customerName: `康是美${row.門市名稱}`,
        customerPhone: row.電話 || '',
        customerEmail: 'taichiuchiu727@gmail.com',
        storeId: row.門市店號?.toString() || '',
        storeName: row.門市名稱 || '',
        storeAddress: row.地址 || '',
        logisticsSubType: 'HSINCHU_HOME',
        goodsAmount: 100,
        goodsName: row.檯帳名稱 || '商品',
        isCollection: 'N',
        remarks: `檯帳編號: ${row.檯帳編號}`,
        deliveryAddress: row.地址 || '',
        weight: 1,
        declaredValue: 0,
        deliveryDate: formattedDeliveryDate  // CHANGED THIS
      };
    });
  };

  // Create batch home delivery orders
  const createBatchHomeDeliveryOrders = async () => {
    const ordersToCreate = convertExcelToOrders();

    if (ordersToCreate.length === 0) {
      showAlert('error', '沒有可處理的訂單');
      return;
    }

    setBatchProcessing(true);
    setBatchProgress(0);
    const results: BatchProcessResult[] = [];

    for (let i = 0; i < ordersToCreate.length; i++) {
      const order = ordersToCreate[i];

      try {
        // ✅ CHANGED: Use api.post instead of fetch
        const response = await api.post('/api/hsinchu/create-order', {
          orderId: order.orderId,
          recipientName: order.customerName,
          recipientPhone: order.customerPhone,
          recipientAddress: order.deliveryAddress,
          weight: order.weight || 1,
          itemCount: 1,
          notes: order.remarks || '',
          declaredValue: order.declaredValue || 0,
          productType: '001',
          deliveryDate: order.deliveryDate || null
        });

        // ✅ CHANGED: Access data directly from response.data
        if (response.data.success && response.data.data) {
          results.push({
            orderId: order.orderId,
            status: 'success',
            trackingNumber: response.data.data.trackingNumber
          });

          const hsinchuResult: LogisticsResult = {
            orderId: order.orderId,
            logisticsId: response.data.data.trackingNumber,
            paymentNo: response.data.data.trackingNumber,
            trackingNumber: response.data.data.trackingNumber,
            labelImage: response.data.data.labelImage,
            destinationCode: response.data.data.destinationCode,
            qrCode: response.data.data.qrCode,
            carrier: '新竹物流',
            storeId: order.storeId,
            storeName: order.storeName,
            logisticsSubType: 'HSINCHU_HOME'
          };

          setOrderResults(prev => [...prev, hsinchuResult]);
        } else {
          results.push({
            orderId: order.orderId,
            status: 'error',
            error: response.data.error || 'Unknown error'
          });
        }
      } catch (error: any) {
        results.push({
          orderId: order.orderId,
          status: 'error',
          error: error.response?.data?.error || error.message
        });
      }

      setBatchProgress(((i + 1) / ordersToCreate.length) * 100);
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setBatchProcessResults(results);
    setBatchProcessing(false);

    const successCount = results.filter(r => r.status === 'success').length;
    const errorCount = results.filter(r => r.status === 'error').length;

    showAlert('info', `批次處理完成: ${successCount} 成功, ${errorCount} 失敗`);
    setActiveStep(4);
  };

  // Download batch results as Excel
  const downloadBatchResults = () => {
    const resultsData = batchProcessResults.map((result, index) => {
      const originalOrder = excelData[index];
      return {
        '訂單編號': result.orderId,
        '門市名稱': originalOrder?.門市名稱 || '',
        '收件人': `康是美${originalOrder?.門市名稱}` || '',
        '電話': originalOrder?.電話 || '',
        '地址': originalOrder?.地址 || '',
        '配送日期': originalOrder?.配送日期 || '未指定',
        '追蹤號碼': result.trackingNumber || '',
        '狀態': result.status === 'success' ? '成功' : '失敗',
        '錯誤訊息': result.error || ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(resultsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '批次結果');
    XLSX.writeFile(wb, `宅配批次結果_${new Date().getTime()}.xlsx`);

    showAlert('success', '批次結果已下載');
  };

  // On page load: check URL params for store selection (returned from ECPay redirect)
  // and restore saved form state from sessionStorage.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const storeId = urlParams.get('storeId') || urlParams.get('CVSStoreID');
    const storeName = urlParams.get('storeName') || urlParams.get('CVSStoreName');
    const storeAddress = urlParams.get('storeAddress') || urlParams.get('CVSAddress');
    const logisticsSubType = urlParams.get('logisticsSubType') || urlParams.get('LogisticsSubType');

    if (storeId && storeName) {
      console.log('Store data found in URL after ECPay redirect:', { storeId, storeName });

      // Restore saved form state first
      try {
        const savedState = sessionStorage.getItem('shippingOrderState');
        if (savedState) {
          const state = JSON.parse(savedState);
          console.log('Restoring form state:', state);
          if (state.currentOrder) setCurrentOrder(state.currentOrder);
          if (state.activeStep !== undefined) setActiveStep(state.activeStep);
          if (state.deliveryMode) setDeliveryMode(state.deliveryMode);
          if (state.homeCarrier) setHomeCarrier(state.homeCarrier);
          if (state.batchMode !== undefined) setBatchMode(state.batchMode);
          if (state.repeatCount !== undefined) setRepeatCount(state.repeatCount);
          sessionStorage.removeItem('shippingOrderState');
        }
      } catch (e) {
        console.error('Error restoring form state:', e);
      }

      // Apply store selection
      const storeData = {
        storeId,
        storeName: decodeURIComponent(storeName),
        storeAddress: storeAddress ? decodeURIComponent(storeAddress) : '',
        logisticsSubType: logisticsSubType || currentOrder.logisticsSubType
      };
      handleStoreDataReceived(storeData);

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Ask the backend whether 順豐 domestic is still on the international product
  // code, so the warning reflects the deployed env rather than a hardcoded guess.
  useEffect(() => {
    if (!isSf) return;
    let cancelled = false;
    api.get('/api/sf-domestic/status')
      .then(res => {
        if (!cancelled && res.data?.success) {
          setSfUsingIntlProduct(Boolean(res.data.data?.usingInternationalProductCode));
        }
      })
      .catch(() => { /* status is advisory only — never block the flow */ });
    return () => { cancelled = true; };
  }, [isSf]);

  // Auto-generate Order ID, set default Product Name, and set Delivery Date to tomorrow
  useEffect(() => {
    if (activeStep === 1 && deliveryMode === 'home' && !batchMode) {
      // Calculate tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const year = tomorrow.getFullYear();
      const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
      const day = String(tomorrow.getDate()).padStart(2, '0');
      const tomorrowString = `${year}-${month}-${day}`;

      setCurrentOrder(prev => ({
        ...prev,
        orderId: prev.orderId || generateOrderId(),
        goodsName: prev.goodsName || '商品',
        deliveryDate: prev.deliveryDate || tomorrowString
      }));
    }
  }, [activeStep, deliveryMode, batchMode]);

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0:
        return true; // Mode selection is always valid
      case 1:
        if (deliveryMode === 'home') {
          if (batchMode) {
            return excelData.length > 0;
          }
          return Boolean(currentOrder.orderId && currentOrder.customerName &&
            currentOrder.customerPhone && currentOrder.goodsName &&
            currentOrder.deliveryAddress);
        } else if (batchMode) {
          return orders.length > 0 && orders.every(order =>
            order.orderId && order.customerName && order.customerPhone && order.goodsName
          );
        } else {
          return Boolean(currentOrder.orderId && currentOrder.customerName &&
            currentOrder.customerPhone && currentOrder.goodsName);
        }
      case 2:
        if (deliveryMode === 'home') {
          return batchMode ? excelData.length > 0 : true;
        }
        return Boolean(selectedStore && selectedStore.storeId);
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    } else {
      showAlert('error', '請填寫所有必填欄位');
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Hex to Base64 conversion for image display
  const hexToBase64 = (hexString: string) => {
    const bytes = [];
    for (let i = 0; i < hexString.length; i += 2) {
      bytes.push(parseInt(hexString.substr(i, 2), 16));
    }

    const uint8Array = new Uint8Array(bytes);
    let binaryString = '';
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i]);
    }

    return btoa(binaryString);
  };

  // Function to display shipping label in new tab
  const displayShippingLabel = (hexImage: string, labelData: HsinchuLabelData = {}) => {
    if (!hexImage) {
      console.error('No image data provided');
      return;
    }

    const base64Image = hexToBase64(hexImage);
    const newWindow = window.open('', '_blank');

    if (!newWindow) {
      showAlert('warning', 'Please allow popups to view the shipping label');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>宅配出貨單 - ${labelData.trackingNumber || '追蹤號碼'}</title>
        <style>
          body {
            margin: 0;
            padding: 20px;
            font-family: Arial, sans-serif;
            background-color: #f5f5f5;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          
          .header {
            text-align: center;
            margin-bottom: 20px;
            background: white;
            padding: 15px;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            width: 100%;
            max-width: 800px;
          }
          
          .header h1 {
            color: #2c3e50;
            margin: 0 0 10px 0;
          }
          
          .tracking-info {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 10px;
            margin-top: 15px;
            text-align: left;
          }
          
          .info-item {
            background: #f8f9fa;
            padding: 8px 12px;
            border-radius: 4px;
            border-left: 3px solid #3498db;
          }
          
          .info-label {
            font-weight: bold;
            color: #2c3e50;
            font-size: 0.9em;
          }
          
          .info-value {
            color: #34495e;
            margin-top: 2px;
          }
          
          .label-container {
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            max-width: 100%;
            overflow: auto;
          }
          
          .shipping-label {
            max-width: 100%;
            height: auto;
            border: 1px solid #ddd;
            border-radius: 4px;
          }
          
          .actions {
            margin-top: 20px;
            text-align: center;
          }
          
          .btn {
            background: #3498db;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            margin: 0 5px;
            transition: background-color 0.3s;
          }
          
          .btn:hover {
            background: #2980b9;
          }
          
          .btn-print {
            background: #27ae60;
          }
          
          .btn-print:hover {
            background: #229954;
          }
          
          .btn-download {
            background: #e74c3c;
          }
          
          .btn-download:hover {
            background: #c0392b;
          }
          
          @media print {
            body {
              background: white;
              padding: 0;
            }
            
            .header, .actions {
              display: none;
            }
            
            .label-container {
              box-shadow: none;
              padding: 0;
              margin: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🚚 宅配出貨單</h1>
          <p>新竹物流 Hsinchu Logistics</p>
          
          ${labelData.trackingNumber ? `
          <div class="tracking-info">
            <div class="info-item">
              <div class="info-label">追蹤號碼</div>
              <div class="info-value">${labelData.trackingNumber}</div>
            </div>
            ${labelData.recipientName ? `
            <div class="info-item">
              <div class="info-label">收件人</div>
              <div class="info-value">${labelData.recipientName}</div>
            </div>
            ` : ''}
            ${labelData.recipientAddress ? `
            <div class="info-item">
              <div class="info-label">收件地址</div>
              <div class="info-value">${labelData.recipientAddress}</div>
            </div>
            ` : ''}
            ${labelData.weight ? `
            <div class="info-item">
              <div class="info-label">重量</div>
              <div class="info-value">${labelData.weight} kg</div>
            </div>
            ` : ''}
          </div>
          ` : ''}
        </div>
        
        <div class="label-container">
          <img src="data:image/png;base64,${base64Image}" 
               alt="Shipping Label" 
               class="shipping-label"
               onload="console.log('Label image loaded successfully')"
               onerror="console.error('Failed to load label image')">
        </div>
        
        <div class="actions">
          <button class="btn btn-print" onclick="printLabel()">🖨️ 列印</button>
          <button class="btn btn-download" onclick="downloadLabel()">💾 下載</button>
          <button class="btn" onclick="window.close()">❌ 關閉</button>
        </div>
        
        <script>
          function printLabel() {
            window.print();
          }
          
          function downloadLabel() {
            const link = document.createElement('a');
            link.href = 'data:image/png;base64,${base64Image}';
            link.download = '宅配出貨單_${labelData.trackingNumber || new Date().getTime()}.png';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          
          window.focus();
        </script>
      </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  // Copy to clipboard function
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        showAlert('success', '已複製到剪貼簿');
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        showAlert('error', '複製失敗');
      });
  };

  // Store selection: navigate same window to ECPay map (no popup).
  // Before navigating, save form state to sessionStorage so it can be restored on return.
  const openStoreMap = async () => {
    setLoading(true);

    // Clear any existing store selection data
    localStorage.removeItem('selectedStore');
    sessionStorage.removeItem('selectedStore');
    setSelectedStore(null);

    try {
      const callbackUrl = `${window.location.origin}/shipping-orders`;

      console.log('Opening store map with callback URL:', callbackUrl);

      const response = await fetch(`${API_BASE}/api/ecpay/logistics/map`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          logisticsSubType: currentOrder.logisticsSubType,
          merchantTradeNo: `MAP${Date.now()}`,
          extraData: callbackUrl
        })
      });

      const result = await response.json();
      console.log('ECPay map response:', result);

      if (result.success) {
        // Save current form state before navigating away
        const stateToSave = {
          currentOrder,
          activeStep,
          deliveryMode,
          homeCarrier,
          batchMode,
          repeatCount,
        };
        sessionStorage.setItem('shippingOrderState', JSON.stringify(stateToSave));

        // Create and submit form in the same window (like storefront does)
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = result.mapUrl;
        form.style.display = 'none';

        Object.entries(result.formData).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = String(value);
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
        // Page navigates to ECPay — on return, useEffect reads URL params
      } else {
        console.error('Failed to get ECPay map URL:', result);
        showAlert('error', result.message || '無法開啟門市地圖');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error opening store map:', error);
      showAlert('error', '開啟門市地圖時發生錯誤');
      setLoading(false);
    }
  };

  const handleStoreDataReceived = (store: any) => {
    setCurrentOrder(prev => ({
      ...prev,
      storeId: store.storeId,
      storeName: store.storeName,
      storeAddress: store.storeAddress || '',
      logisticsSubType: store.logisticsSubType || prev.logisticsSubType
    }));

    setSelectedStore(store);
    showAlert('success', `已選擇 ${store.storeName} 作為取貨門市`);
    setLoading(false);
  };

  // Create Hsinchu home delivery order (supports single or repeat)
  const createHsinchuHomeOrder = async () => {
    setSubmitLoading(true);
    const results: LogisticsResult[] = [];
    const isBatch = repeatCount > 1;

    if (isBatch) {
      setBatchProcessing(true);
      setBatchProgress(0);
    }

    try {
      // Loop for repeat count
      for (let i = 0; i < repeatCount; i++) {
        // Generate a new Order ID for each repeat, but keep the first one as is if it's the first iteration
        // NOTE: If user manually entered an ID, we should probably append a suffix or generate new ones
        // For simplicity, generate new unique IDs for repeats 2..N, or all if we want to be safe

        let thisOrderId = currentOrder.orderId;
        if (i > 0) {
          thisOrderId = generateOrderId(); // Generate new ID for subsequent copies
        }

        // ✅ CHANGED: Use api.post instead of fetch
        const response = await api.post('/api/hsinchu/create-order', {
          orderId: thisOrderId,
          recipientName: currentOrder.customerName,
          recipientPhone: currentOrder.customerPhone,
          recipientAddress: currentOrder.deliveryAddress,
          weight: currentOrder.weight || 1,
          itemCount: 1,
          notes: currentOrder.remarks || '',
          declaredValue: currentOrder.declaredValue || 0,
          productType: '001',
          deliveryDate: currentOrder.deliveryDate || null
        });

        // ✅ CHANGED: Access data directly from response.data
        if (response.data.success && response.data.data) {
          const hsinchuResult: LogisticsResult = {
            orderId: thisOrderId,
            logisticsId: response.data.data.trackingNumber,
            paymentNo: response.data.data.trackingNumber,
            trackingNumber: response.data.data.trackingNumber,
            labelImage: response.data.data.labelImage,
            destinationCode: response.data.data.destinationCode,
            qrCode: response.data.data.qrCode,
            carrier: '新竹物流',
            storeId: '',
            storeName: '',
            logisticsSubType: 'HSINCHU_HOME'
          };
          results.push(hsinchuResult);
        } else {
          console.error(`Failed iteration ${i + 1}:`, response.data.error);
          showAlert('error', `第 ${i + 1} 筆失敗: ${response.data.error}`);
        }

        // Update progress if batch
        if (isBatch) {
          setBatchProgress(((i + 1) / repeatCount) * 100);
          await new Promise(resolve => setTimeout(resolve, 500)); // Small delay
        }
      }

      if (results.length > 0) {
        setOrderResults(results);
        if (isBatch) {
          showAlert('success', `成功建立 ${results.length} 筆宅配單`);
        } else {
          showAlert('success', `宅配出貨單建立成功！追蹤號碼: ${results[0].trackingNumber}`);
        }
        setActiveStep(4);
      } else {
        throw new Error('Failed to create any orders');
      }

    } catch (error: any) {
      console.error('Error creating Hsinchu order:', error);
      showAlert('error', '建立宅配出貨單失敗: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitLoading(false);
      if (isBatch) setBatchProcessing(false);
    }
  };

  // Create 順豐 domestic (TW→TW) home delivery order. Same repeat-count
  // behaviour as the Hsinchu flow; SF returns a hosted PDF label instead of a
  // hex image, so results carry labelUrl rather than labelImage.
  const createSfHomeOrder = async () => {
    setSubmitLoading(true);
    const results: LogisticsResult[] = [];
    const isBatch = repeatCount > 1;

    if (isBatch) {
      setBatchProcessing(true);
      setBatchProgress(0);
    }

    try {
      for (let i = 0; i < repeatCount; i++) {
        const thisOrderId = i === 0 ? currentOrder.orderId : generateOrderId();

        const response = await api.post('/api/sf-domestic/create-order', {
          orderId: thisOrderId,
          recipientName: currentOrder.customerName,
          recipientPhone: currentOrder.customerPhone,
          recipientAddress: currentOrder.deliveryAddress,
          recipientEmail: currentOrder.customerEmail || '',
          weight: currentOrder.weight || 1,
          goodsAmount: currentOrder.goodsAmount,
          goodsName: currentOrder.goodsName,
          declaredValue: currentOrder.declaredValue || currentOrder.goodsAmount,
          notes: currentOrder.remarks || ''
        });

        if (response.data.success && response.data.data) {
          const d = response.data.data;
          if (d.usingInternationalProductCode) setSfUsingIntlProduct(true);
          results.push({
            orderId: thisOrderId,
            logisticsId: d.sfWaybillNo,
            paymentNo: d.sfWaybillNo,
            trackingNumber: d.sfWaybillNo,
            labelUrl: d.labelUrl,
            invoiceUrl: d.invoiceUrl,
            carrier: d.carrier || '順豐速運',
            storeId: '',
            storeName: '',
            logisticsSubType: 'SF_HOME'
          });
        } else {
          console.error(`SF failed iteration ${i + 1}:`, response.data.error);
          showAlert('error', `第 ${i + 1} 筆失敗: ${response.data.error}`);
        }

        if (isBatch) {
          setBatchProgress(((i + 1) / repeatCount) * 100);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      if (results.length > 0) {
        setOrderResults(results);
        showAlert('success', isBatch
          ? `成功建立 ${results.length} 筆順豐宅配單`
          : `順豐宅配單建立成功！運單號碼: ${results[0].trackingNumber}`);
        setActiveStep(4);
      } else {
        throw new Error('Failed to create any orders');
      }
    } catch (error: any) {
      console.error('Error creating SF domestic order:', error);
      showAlert('error', '建立順豐宅配單失敗: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitLoading(false);
      if (isBatch) setBatchProcessing(false);
    }
  };

  // Open the SF hosted PDF label in a new tab
  const printSfLabel = (orderResult: LogisticsResult) => {
    if (!orderResult.labelUrl) {
      showAlert('error', '沒有可用的順豐面單連結');
      return;
    }
    const win = window.open(orderResult.labelUrl, '_blank');
    if (!win) showAlert('warning', '請允許彈出視窗以開啟面單');
  };

  // Print Hsinchu label
  const printHsinchuLabel = (orderResult: LogisticsResult) => {
    if (orderResult.labelImage) {
      displayShippingLabel(orderResult.labelImage, {
        trackingNumber: orderResult.trackingNumber,
        recipientName: currentOrder.customerName,
        recipientAddress: currentOrder.deliveryAddress,
        weight: currentOrder.weight
      });
    } else {
      showAlert('error', '沒有可用的出貨單圖像');
    }
  };

  // Create single order (pickup or home delivery)
  const createSingleOrder = async () => {
    if (deliveryMode === 'home') {
      if (homeCarrier === 'sf') {
        await createSfHomeOrder();
      } else {
        await createHsinchuHomeOrder();
      }
    } else {
      setSubmitLoading(true);
      try {
        if (!selectedStore?.storeId || !selectedStore?.storeName || !selectedStore?.logisticsSubType) {
          throw new Error('Store information is incomplete. Please select a store again.');
        }

        if (!currentOrder.orderId || !currentOrder.customerName || !currentOrder.customerPhone) {
          throw new Error('Order information is incomplete. Please fill in all required fields.');
        }

        const orderData = {
          orderId: currentOrder.orderId,
          customerName: currentOrder.customerName,
          customerPhone: currentOrder.customerPhone,
          customerEmail: currentOrder.customerEmail || '',
          storeId: selectedStore.storeId,
          storeName: selectedStore.storeName,
          logisticsSubType: selectedStore.logisticsSubType,
          goodsAmount: currentOrder.goodsAmount,
          goodsName: currentOrder.goodsName,
          isCollection: currentOrder.isCollection,
          source: 'manual'
        };

        console.log('=== SENDING REQUEST ===');
        console.log('Request payload:', JSON.stringify(orderData, null, 2));

        // ✅ CHANGED: Use api.post instead of fetch
        const response = await api.post('/api/ecpay/logistics/create-direct', orderData);

        console.log('Success result:', response.data);

        // ✅ CHANGED: Access data directly from response.data
        if (response.data.success) {
          setOrderResults([response.data.data]);
          showAlert('success', '物流訂單建立成功！');
          setActiveStep(4);
        } else {
          throw new Error(response.data.message || '建立物流訂單失敗');
        }
      } catch (error: any) {
        console.error('=== FINAL ERROR ===');
        console.error('Error details:', error);
        showAlert('error', '建立訂單失敗: ' + (error.response?.data?.error || error.message));
      }
      setSubmitLoading(false);
    }
  };

  // Print single label (pickup or home delivery)
  const printSingleLabel = async (orderResult: LogisticsResult) => {
    if (orderResult.logisticsSubType === 'SF_HOME') {
      printSfLabel(orderResult);
    } else if (orderResult.logisticsSubType === 'HSINCHU_HOME') {
      printHsinchuLabel(orderResult);
    } else {
      try {
        showAlert('info', '正在準備出貨單...');

        // ✅ CHANGED: Use api.post with responseType
        const response = await api.post('/api/ecpay/logistics/print-direct', {
          logisticsId: orderResult.logisticsId,
          paymentNo: orderResult.paymentNo,
          validationNo: orderResult.validationNo,
          logisticsSubType: orderResult.logisticsSubType,
          storeId: orderResult.storeId
        }, {
          responseType: 'text' // ✅ Important: tell axios to expect HTML
        });

        // ✅ CHANGED: Access data directly from response.data
        const htmlContent = response.data;
        const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        if (printWindow) {
          printWindow.document.write(htmlContent);
          printWindow.document.close();
          showAlert('success', '出貨單已在新視窗中開啟');
        } else {
          showAlert('error', '無法開啟新視窗，請檢查瀏覽器彈出視窗設定');
        }
      } catch (error: any) {
        console.error('Print error:', error);
        showAlert('error', '列印出貨單時發生錯誤: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const resetForm = () => {
    setActiveStep(0);
    setBatchMode(false);
    setDeliveryMode('pickup');
    setHomeCarrier('hsinchu');
    setOrders([]);
    setOrderResults([]);
    setExcelFile(null);
    setExcelData([]);
    setExcelPreview([]);
    setBatchProcessResults([]);
    setBatchProcessing(false);
    setBatchProgress(0);
    setCurrentOrder({
      orderId: '',
      customerName: '',
      customerPhone: '',
      customerEmail: 'taichiuchiu727@gmail.com',
      storeId: '',
      storeName: '',
      storeAddress: '',
      logisticsSubType: 'UNIMARTC2C',
      goodsAmount: 100,
      goodsName: '',
      isCollection: 'N',
      remarks: '',
      deliveryAddress: '',
      weight: 1,
      declaredValue: 0
    });
    setSelectedStore(null);
  };

  // Quick return order function
  // Quick return order function
  const createQuickReturn = async () => {
    setSubmitLoading(true);

    // Generate random order ID and product name
    const randomOrderId = generateOrderId();
    const randomProducts = ['電子產品', '服飾用品', '生活用品', '書籍文具', '美妝保養', '食品零食'];
    const randomProductName = randomProducts[Math.floor(Math.random() * randomProducts.length)];

    // Use the selected store from currentOrder state, default to 國企 if not set
    const selectedStore = {
      storeId: currentOrder.storeId || '281238',
      storeName: currentOrder.storeName || '7-ELEVEN 國企門市',
      logisticsSubType: 'UNIMARTC2C'
    };

    const returnOrderData = {
      orderId: randomOrderId,
      customerName: '林成鄆',
      customerPhone: '0922158956',
      customerEmail: 'taichiuchiu727@gmail.com',
      storeId: selectedStore.storeId,
      storeName: selectedStore.storeName,
      logisticsSubType: selectedStore.logisticsSubType,
      goodsAmount: 100,
      goodsName: randomProductName,
      isCollection: 'N',
      source: 'quick_return'
    };

    try {
      console.log('Creating quick return order:', returnOrderData);

      const response = await api.post('/api/ecpay/logistics/create-direct', returnOrderData);
      console.log('Quick return response:', response.data);

      const result = response.data;

      if (result.success) {
        // Show success message with tracking number
        showAlert('success', `退貨訂單建立成功！追蹤號碼: ${result.data.logisticsId}`);

        // Copy tracking number to clipboard
        if (result.data.logisticsId) {
          copyToClipboard(result.data.logisticsId);
        }

        setOrderResults([result.data]);
        setActiveStep(4);
      } else {
        throw new Error(result.message || '建立退貨訂單失敗');
      }
    } catch (error) {
      console.error('Quick return error:', error);
      showAlert('error', '建立退貨訂單失敗: ' + (error as Error).message);
    }

    setSubmitLoading(false);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                <Speed sx={{ mr: 1, verticalAlign: 'middle' }} />
                選擇處理模式
              </Typography>
            </Grid>

            {/* Delivery Mode Selection */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    配送方式
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={deliveryMode === 'pickup' ? 'pickup' : `home-${homeCarrier}`}
                      onChange={(e) => {
                        const v = e.target.value as 'pickup' | 'home-hsinchu' | 'home-sf';
                        if (v === 'pickup') {
                          setDeliveryMode('pickup');
                          return;
                        }
                        setDeliveryMode('home');
                        setHomeCarrier(v === 'home-sf' ? 'sf' : 'hsinchu');
                        // Excel 批次 is the 康是美 / 新竹物流 flow only.
                        if (v === 'home-sf') setBatchMode(false);
                      }}
                    >
                      <MenuItem value="pickup">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Store />
                          超商取貨
                        </Box>
                      </MenuItem>
                      <MenuItem value="home-hsinchu">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Home />
                          宅配到府 (新竹物流)
                        </Box>
                      </MenuItem>
                      <MenuItem value="home-sf">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalShipping />
                          宅配到府 (順豐速運)
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {isSf && (
                    <Alert severity={sfUsingIntlProduct ? 'warning' : 'info'} sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        順豐宅配為單筆建單（不支援 Excel 批次），建立後會回傳 PDF 面單連結。
                        {sfUsingIntlProduct && (
                          <>
                            <br />
                            <strong>⚠️ 目前仍使用國際產品代碼 INT0014</strong>：順豐尚未提供「台灣宅配」
                            專用代碼，面單會附帶報關發票，運費可能以<strong>國際出口費率</strong>計價。
                          </>
                        )}
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Batch Mode */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={batchMode}
                    onChange={(e) => setBatchMode(e.target.checked)}
                    color="primary"
                    disabled={isSf}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {batchMode ? '批次處理模式' : '單筆處理模式'}
                    <Chip
                      size="small"
                      label={batchMode ? '批次' : '單筆'}
                      color={batchMode ? 'secondary' : 'primary'}
                    />
                  </Box>
                }
              />
              {deliveryMode === 'home' && batchMode && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Excel 批次上傳功能</strong><br />
                    • 支援上傳 Excel 檔案批次建立宅配訂單<br />
                    • 自動產生「康是美 + 門市名稱」作為收件人<br />
                    • 必須包含欄位：門市名稱、電話、地址
                  </Typography>
                </Alert>
              )}
            </Grid>

            {/* Quick Return Button */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: 'warning.50', borderColor: 'warning.200' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Speed color="warning" />
                    快速退貨
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    一鍵建立退貨訂單 - 使用預設資訊立即產生追蹤號碼
                  </Typography>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>選擇退貨門市</InputLabel>
                    <Select
                      value={currentOrder.storeId || '281238'}
                      onChange={(e) => {
                        const selectedStoreId = e.target.value;
                        const storeMap: { [key: string]: { name: string; id: string } } = {
                          '281238': { name: '國企', id: '281238' },
                          '185888': { name: '彩龍', id: '185888' }
                        };
                        const selected = storeMap[selectedStoreId];
                        setCurrentOrder(prev => ({
                          ...prev,
                          storeId: selected.id,
                          storeName: `7-ELEVEN ${selected.name}門市`
                        }));
                      }}
                      label="選擇退貨門市"
                    >
                      <MenuItem value="281238">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Store />
                          國企門市 (281238)
                        </Box>
                      </MenuItem>
                      <MenuItem value="185888">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Store />
                          彩龍門市 (185888)
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Chip size="small" label="收件人: 林成鄆" />
                    <Chip size="small" label="電話: 0922158956" />
                    <Chip size="small" label={`門市: ${currentOrder.storeName || '7-ELEVEN 國企門市'}`} />
                    <Chip size="small" label="隨機訂單編號" />
                  </Box>

                  <Button
                    variant="contained"
                    color="warning"
                    onClick={createQuickReturn}
                    disabled={submitLoading}
                    startIcon={submitLoading ? <CircularProgress size={20} /> : <Speed />}
                    size="large"
                  >
                    {submitLoading ? '建立中...' : '退貨 Default'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {deliveryMode === 'home' ? '宅配到府' : (batchMode ? '批次處理模式' : '單筆處理模式')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {deliveryMode === 'home'
                      ? `使用${carrierName}直接配送到客戶指定地址，支援全台宅配服務。`
                      : batchMode
                        ? '適合一次處理多筆訂單，可以批次列印出貨單，提高作業效率。'
                        : '適合處理單筆訂單，可以立即建立並列印出貨單。'
                    }
                  </Typography>

                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {deliveryMode === 'home' ? (
                      <>
                        <Chip size="small" icon={<Home />} label="宅配到府" />
                        <Chip size="small" icon={<LocalShipping />} label={carrierName} />
                        <Chip size="small" icon={<Speed />} label="全台配送" />
                      </>
                    ) : batchMode ? (
                      <>
                        <Chip size="small" icon={<Add />} label="批次新增" />
                        <Chip size="small" icon={<Print />} label="批次列印" />
                        <Chip size="small" icon={<Speed />} label="高效率" />
                      </>
                    ) : (
                      <>
                        <Chip size="small" icon={<Speed />} label="快速處理" />
                        <Chip size="small" icon={<Print />} label="即時列印" />
                        <Chip size="small" icon={<CheckCircle />} label="簡單操作" />
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 1:
        if (deliveryMode === 'home') {
          if (batchMode) {
            // Excel batch upload for home delivery
            return (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    <CloudUpload sx={{ mr: 1, verticalAlign: 'middle' }} />
                    上傳Excel批次訂單
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <input
                          accept=".xlsx,.xls"
                          style={{ display: 'none' }}
                          id="excel-upload"
                          type="file"
                          onChange={handleExcelUpload}
                        />
                        <label htmlFor="excel-upload">
                          <Button
                            variant="contained"
                            component="span"
                            startIcon={<Upload />}
                            size="large"
                          >
                            選擇Excel檔案
                          </Button>
                        </label>

                        {excelFile && (
                          <Typography variant="body2" sx={{ mt: 2 }}>
                            已選擇: {excelFile.name}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {excelPreview.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          資料預覽 (前10筆)
                        </Typography>

                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>門市名稱</TableCell>
                                <TableCell>收件人</TableCell>
                                <TableCell>電話</TableCell>
                                <TableCell>地址</TableCell>
                                <TableCell>配送日期</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {excelPreview.map((row, index) => (
                                <TableRow key={index}>
                                  <TableCell>{row.門市名稱}</TableCell>
                                  <TableCell>康是美{row.門市名稱}</TableCell>
                                  <TableCell>{row.電話}</TableCell>
                                  <TableCell>{row.地址}</TableCell>
                                  <TableCell>{row.配送日期 || '未指定'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            共 {excelData.length} 筆訂單
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<Refresh />}
                            onClick={() => {
                              setExcelFile(null);
                              setExcelData([]);
                              setExcelPreview([]);
                            }}
                          >
                            重新上傳
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            );
          }

          // Single home delivery form
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  <Home sx={{ mr: 1, verticalAlign: 'middle' }} />
                  宅配訂單資訊
                </Typography>
              </Grid>

              {/* Presets and Repeat Count */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    快速填寫 (點擊自動帶入)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {HOME_DELIVERY_PRESETS.map((preset) => (
                      <Chip
                        key={preset.label}
                        label={preset.label}
                        onClick={() => {
                          setCurrentOrder(prev => ({
                            ...prev,
                            customerName: (preset as { name?: string }).name ?? preset.label,
                            customerPhone: preset.phone,
                            deliveryAddress: preset.address
                          }));
                          showAlert('success', `已帶入 ${preset.label} 資料`);
                        }}
                        color="primary"
                        variant="outlined"
                        clickable
                      />
                    ))}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="建立數量 (批次產生)"
                        type="number"
                        value={repeatCount}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (val >= 1 && val <= 50) {
                            setRepeatCount(val);
                          }
                        }}
                        inputProps={{ min: 1, max: 50 }}
                        helperText="設定大於1時，將自動產生多筆不同單號的訂單"
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="訂單編號"
                  value={currentOrder.orderId}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, orderId: e.target.value }))}
                  required
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        onClick={() => setCurrentOrder(prev => ({ ...prev, orderId: generateOrderId() }))}
                      >
                        自動產生
                      </Button>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="商品名稱"
                  value={currentOrder.goodsName}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, goodsName: e.target.value }))}
                  required
                  inputProps={{ maxLength: 25 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="收件人姓名"
                  value={currentOrder.customerName}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, customerName: e.target.value }))}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="收件人電話"
                  value={currentOrder.customerPhone}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, customerPhone: e.target.value }))}
                  required
                  placeholder="09xxxxxxxx"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="宅配地址"
                  value={currentOrder.deliveryAddress}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, deliveryAddress: e.target.value }))}
                  required
                  multiline
                  rows={2}
                  placeholder="請輸入完整的宅配地址，包含郵遞區號"
                />
              </Grid>

              {/* 指定配送日期 is a 新竹物流 field — SF's create-order payload has no
                  equivalent, so don't offer a control that silently does nothing. */}
              {!isSf && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="指定配送日期 (選填)"
                    type="date"
                    value={currentOrder.deliveryDate}
                    onChange={(e) => setCurrentOrder(prev => ({ ...prev, deliveryDate: e.target.value }))}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      min: new Date().toISOString().split('T')[0] // Minimum is today
                    }}
                    helperText="留空表示不指定日期，最快送達"
                  />
                </Grid>
              )}

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="重量 (kg)"
                  type="number"
                  value={currentOrder.weight}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, weight: Number(e.target.value) }))}
                  inputProps={{ min: 0.1, max: 20, step: 0.1 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="商品金額"
                  type="number"
                  value={currentOrder.goodsAmount}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, goodsAmount: Number(e.target.value) }))}
                  required
                  inputProps={{ min: 1, max: 20000 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="申報價值"
                  type="number"
                  value={currentOrder.declaredValue}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, declaredValue: Number(e.target.value) }))}
                  inputProps={{ min: 0, max: 50000 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>付款方式</InputLabel>
                  <Select
                    value={currentOrder.isCollection}
                    onChange={(e) => setCurrentOrder(prev => ({ ...prev, isCollection: e.target.value as 'Y' | 'N' }))}
                    label="付款方式"
                  >
                    <MenuItem value="N">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle color="success" />
                        不需付款 (已付款)
                      </Box>
                    </MenuItem>
                    <MenuItem value="Y">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Receipt color="warning" />
                        貨到付款 (COD)
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="收件人Email"
                  value={currentOrder.customerEmail}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, customerEmail: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="備註"
                  value={currentOrder.remarks}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, remarks: e.target.value }))}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          );
        } else if (batchMode) {
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
                  批次訂單管理
                </Typography>
              </Grid>

              {/* Current Order Input - same as before */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      新增訂單
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="訂單編號"
                          value={currentOrder.orderId}
                          onChange={(e) => setCurrentOrder(prev => ({ ...prev, orderId: e.target.value }))}
                          required
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <Button
                                size="small"
                                onClick={() => setCurrentOrder(prev => ({ ...prev, orderId: generateOrderId() }))}
                              >
                                自動產生
                              </Button>
                            )
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="商品名稱"
                          value={currentOrder.goodsName}
                          onChange={(e) => setCurrentOrder(prev => ({ ...prev, goodsName: e.target.value }))}
                          required
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="收件人姓名"
                          value={currentOrder.customerName}
                          onChange={(e) => setCurrentOrder(prev => ({ ...prev, customerName: e.target.value }))}
                          required
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="收件人電話"
                          value={currentOrder.customerPhone}
                          onChange={(e) => setCurrentOrder(prev => ({ ...prev, customerPhone: e.target.value }))}
                          required
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="商品金額"
                          type="number"
                          value={currentOrder.goodsAmount}
                          onChange={(e) => setCurrentOrder(prev => ({ ...prev, goodsAmount: Number(e.target.value) }))}
                          required
                          size="small"
                          inputProps={{ min: 1, max: 20000 }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>付款方式</InputLabel>
                          <Select
                            value={currentOrder.isCollection}
                            onChange={(e) => setCurrentOrder(prev => ({ ...prev, isCollection: e.target.value as 'Y' | 'N' }))}
                            label="付款方式"
                          >
                            <MenuItem value="N">不需付款 (已付款)</MenuItem>
                            <MenuItem value="Y">貨到付款 (COD)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="備註"
                          value={currentOrder.remarks}
                          onChange={(e) => setCurrentOrder(prev => ({ ...prev, remarks: e.target.value }))}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => {
                            if (!currentOrder.orderId || !currentOrder.customerName || !currentOrder.customerPhone || !currentOrder.goodsName) {
                              showAlert('error', '請填寫所有必填欄位');
                              return;
                            }

                            setOrders(prev => [...prev, { ...currentOrder }]);

                            // Reset current order for next entry
                            setCurrentOrder(prev => ({
                              ...prev,
                              orderId: '',
                              customerName: '',
                              customerPhone: '',
                              goodsName: '',
                              remarks: ''
                            }));

                            showAlert('success', '訂單已加入批次清單');
                          }}
                          disabled={!currentOrder.orderId || !currentOrder.customerName || !currentOrder.customerPhone || !currentOrder.goodsName}
                        >
                          加入批次清單
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Batch Orders List */}
              {orders.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        批次清單 ({orders.length} 筆訂單)
                      </Typography>

                      {orders.map((order, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            mb: 1,
                            bgcolor: 'grey.50',
                            borderRadius: 1
                          }}
                        >
                          <Box>
                            <Typography variant="body2">
                              <strong>{order.orderId}</strong> - {order.goodsName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {order.customerName} / {order.customerPhone} / NT${order.goodsAmount}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setOrders(prev => prev.filter((_, i) => i !== index))}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          );
        } else {
          // Single order mode for pickup
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
                  訂單資訊
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="訂單編號"
                  value={currentOrder.orderId}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, orderId: e.target.value }))}
                  required
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        onClick={() => setCurrentOrder(prev => ({ ...prev, orderId: generateOrderId() }))}
                      >
                        自動產生
                      </Button>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="商品金額"
                  type="number"
                  value={currentOrder.goodsAmount}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, goodsAmount: Number(e.target.value) }))}
                  required
                  inputProps={{ min: 1, max: 20000 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="商品名稱"
                  value={currentOrder.goodsName}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, goodsName: e.target.value }))}
                  required
                  inputProps={{ maxLength: 25 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="收件人姓名"
                  value={currentOrder.customerName}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, customerName: e.target.value }))}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="收件人電話"
                  value={currentOrder.customerPhone}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, customerPhone: e.target.value }))}
                  required
                  placeholder="09xxxxxxxx"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>付款方式</InputLabel>
                  <Select
                    value={currentOrder.isCollection}
                    onChange={(e) => setCurrentOrder(prev => ({ ...prev, isCollection: e.target.value as 'Y' | 'N' }))}
                    label="付款方式"
                  >
                    <MenuItem value="N">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle color="success" />
                        不需付款 (已付款)
                      </Box>
                    </MenuItem>
                    <MenuItem value="Y">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Receipt color="warning" />
                        貨到付款 (COD)
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="收件人Email"
                  value={currentOrder.customerEmail}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, customerEmail: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="備註"
                  value={currentOrder.remarks}
                  onChange={(e) => setCurrentOrder(prev => ({ ...prev, remarks: e.target.value }))}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          );
        }

      case 2:
        if (deliveryMode === 'home') {
          if (batchMode) {
            // Batch confirmation for home delivery
            return (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                    確認批次訂單
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        批次訂單摘要
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <Chip label={`總訂單數: ${excelData.length}`} color="primary" />
                        <Chip label="配送方式: 新竹物流宅配" color="secondary" />
                        <Chip label="自動產生訂單編號" />
                      </Box>

                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>注意事項：</strong><br />
                          • 將建立 {excelData.length} 筆宅配訂單<br />
                          • 每筆訂單會自動產生追蹤號碼<br />
                          • 處理時間約 {Math.ceil(excelData.length * 0.5)} 秒<br />
                          • 請確保網路連線穩定
                        </Typography>
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            );
          }

          // Single home delivery confirmation
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                  確認宅配資訊
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Home />
                      宅配訂單資訊
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">訂單編號</Typography>
                        <Typography variant="body1">
                          {currentOrder.orderId}
                          {repeatCount > 1 && <Chip size="small" label={`+${repeatCount - 1} 筆 (共 ${repeatCount} 筆)`} color="primary" sx={{ ml: 1 }} />}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">收件人</Typography>
                        <Typography variant="body1">{currentOrder.customerName}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">商品名稱</Typography>
                        <Typography variant="body1">{currentOrder.goodsName}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">聯絡電話</Typography>
                        <Typography variant="body1">{currentOrder.customerPhone}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">宅配地址</Typography>
                        <Typography variant="body1">{currentOrder.deliveryAddress}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">重量</Typography>
                        <Typography variant="body1">{currentOrder.weight} kg</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">商品金額</Typography>
                        <Typography variant="body1">NT$ {currentOrder.goodsAmount}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">付款方式</Typography>
                        <Typography variant="body1">
                          {currentOrder.isCollection === 'Y' ? '貨到付款' : '已付款'}
                        </Typography>
                      </Grid>

                      {currentOrder.deliveryDate && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">指定配送日期</Typography>
                          <Typography variant="body1">{currentOrder.deliveryDate}</Typography>
                        </Grid>
                      )}
                    </Grid>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>配送資訊：</strong><br />
                        • 配送業者：{carrierName}<br />
                        • 預計配送時間：1-3個工作天<br />
                        • 配送範圍：全台灣地區<br />
                        • 建立後將產生追蹤號碼及出貨單
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          );
        } else {
          // Store selection for pickup orders
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
                  物流配送方式
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>物流類型</InputLabel>
                  <Select
                    value={currentOrder.logisticsSubType}
                    onChange={(e) => setCurrentOrder(prev => ({ ...prev, logisticsSubType: e.target.value }))}
                    label="物流類型"
                  >
                    {logisticsOptions.filter(option => !['HSINCHU_HOME', 'SF_HOME'].includes(option.value)).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{option.icon}</span>
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<Store />}
                    onClick={openStoreMap}
                    disabled={!currentOrder.logisticsSubType || loading}
                  >
                    {loading ? <CircularProgress size={20} /> : '選擇門市'}
                  </Button>

                  {selectedStore && (
                    <Chip
                      label={`已選擇: ${selectedStore.storeName}`}
                      color="success"
                      variant="outlined"
                      icon={<CheckCircle />}
                    />
                  )}
                </Box>
              </Grid>

              {selectedStore && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        選擇的門市資訊
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>門市代號:</strong> {selectedStore.storeId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>門市名稱:</strong> {selectedStore.storeName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>門市地址:</strong> {selectedStore.storeAddress}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          );
        }

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                處理訂單
              </Typography>
            </Grid>

            {!batchProcessing && batchProcessResults.length === 0 && (
              <>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {deliveryMode === 'home' && batchMode ? '批次宅配訂單摘要' :
                          deliveryMode === 'home' ? '宅配訂單摘要' :
                            batchMode ? '批次訂單摘要' : '訂單摘要'}
                      </Typography>

                      {deliveryMode === 'home' && batchMode ? (
                        <Box>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                            <Chip label={`總訂單數: ${excelData.length}`} color="primary" />
                            <Chip label="配送方式: 新竹物流宅配" color="secondary" />
                          </Box>
                        </Box>
                      ) : deliveryMode === 'home' ? (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">訂單編號</Typography>
                            <Typography variant="body1">{currentOrder.orderId}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">收件人</Typography>
                            <Typography variant="body1">{currentOrder.customerName}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">商品名稱</Typography>
                            <Typography variant="body1">{currentOrder.goodsName}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">配送方式</Typography>
                            <Typography variant="body1">{carrierName}宅配</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">宅配地址</Typography>
                            <Typography variant="body1">{currentOrder.deliveryAddress}</Typography>
                          </Grid>
                        </Grid>
                      ) : batchMode ? (
                        <Box>
                          <Typography variant="body1" gutterBottom>
                            共 {orders.length} 筆訂單
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            取貨門市: {selectedStore?.storeName}
                          </Typography>

                          <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 2 }}>
                            {orders.slice(0, 5).map((order, index) => (
                              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                • {order.orderId} - {order.customerName} - {order.goodsName}
                              </Typography>
                            ))}
                            {orders.length > 5 && (
                              <Typography variant="body2" color="text.secondary">
                                ... 還有 {orders.length - 5} 筆訂單
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">訂單編號</Typography>
                            <Typography variant="body1">{currentOrder.orderId}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">收件人</Typography>
                            <Typography variant="body1">{currentOrder.customerName}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">商品名稱</Typography>
                            <Typography variant="body1">{currentOrder.goodsName}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">取貨門市</Typography>
                            <Typography variant="body1">{selectedStore?.storeName}</Typography>
                          </Grid>
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={deliveryMode === 'home' && batchMode ? createBatchHomeDeliveryOrders : createSingleOrder}
                      disabled={submitLoading}
                      startIcon={submitLoading ? <CircularProgress size={20} /> : <Save />}
                    >
                      {submitLoading ? '建立中...' :
                        deliveryMode === 'home' && batchMode ? `開始建立 ${excelData.length} 筆訂單` :
                          deliveryMode === 'home' ? (repeatCount > 1 ? `建立 ${repeatCount} 筆宅配訂單` : '建立宅配訂單') :
                            batchMode ? '建立批次訂單' : '建立物流訂單'}
                    </Button>
                  </Box>
                </Grid>
              </>
            )}

            {batchProcessing && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      處理中...
                    </Typography>
                    <LinearProgress variant="determinate" value={batchProgress} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {Math.round(batchProgress)}% 完成
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        );

      case 4:
        return orderResults.length > 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {deliveryMode === 'home' && batchMode ? '批次宅配訂單建立完成！' :
                  deliveryMode === 'home' ? '宅配訂單建立成功！' :
                    batchMode ? '批次物流訂單建立成功！' : '物流訂單建立成功！'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {deliveryMode === 'home' ? '宅配訂單資訊' : '物流訂單資訊'} ({orderResults.length} 筆)
                    </Typography>
                    {batchMode && batchProcessResults.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                          label={`成功: ${batchProcessResults.filter(r => r.status === 'success').length}`}
                          color="success"
                          size="small"
                        />
                        <Chip
                          label={`失敗: ${batchProcessResults.filter(r => r.status === 'error').length}`}
                          color="error"
                          size="small"
                        />
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          startIcon={<Print />}
                          onClick={() => {
                            // Print all labels
                            orderResults.forEach((result, index) => {
                              if (result.labelImage || (result.logisticsId && result.paymentNo)) {
                                setTimeout(() => {
                                  printSingleLabel(result);
                                }, index * 500); // Delay each print by 500ms to avoid overwhelming the browser
                              }
                            });
                            showAlert('info', `正在開啟 ${orderResults.length} 張出貨單...`);
                          }}
                        >
                          批次列印全部
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FileDownload />}
                          onClick={downloadBatchResults}
                        >
                          下載結果
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {deliveryMode === 'home' && batchMode ? (
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>訂單編號</TableCell>
                            <TableCell>門市</TableCell>
                            <TableCell>追蹤號碼</TableCell>
                            <TableCell>狀態</TableCell>
                            <TableCell>操作</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orderResults.map((result, index) => (
                            <TableRow key={index}>
                              <TableCell>{result.orderId}</TableCell>
                              <TableCell>{result.storeName}</TableCell>
                              <TableCell>
                                {result.trackingNumber}
                                {result.trackingNumber && (
                                  <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(result.trackingNumber || '')}
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={batchProcessResults[index]?.status === 'success' ? '成功' : '失敗'}
                                  color={batchProcessResults[index]?.status === 'success' ? 'success' : 'error'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {result.labelImage && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Print />}
                                    onClick={() => printHsinchuLabel(result)}
                                  >
                                    列印
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <>
                      {orderResults.length > 1 && (
                        <Box sx={{ mb: 2, textAlign: 'right' }}>
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            startIcon={<Print />}
                            onClick={() => {
                              orderResults.forEach((result, index) => {
                                setTimeout(() => {
                                  printSingleLabel(result);
                                }, index * 500);
                              });
                              showAlert('info', `正在開啟 ${orderResults.length} 張出貨單...`);
                            }}
                          >
                            批次列印全部 ({orderResults.length} 張)
                          </Button>
                        </Box>
                      )}
                      <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {orderResults.map((result, index) => {
                          // Create combined tracking code for pickup orders
                          const combinedCode = deliveryMode === 'pickup' && result.paymentNo && result.validationNo
                            ? `${result.paymentNo}${result.validationNo}`
                            : null;

                          return (
                            <Box key={index} sx={{
                              p: 2,
                              mb: 2,
                              border: 1,
                              borderColor: 'grey.200',
                              borderRadius: 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Box>
                                <Typography variant="body1">
                                  <strong>{result.orderId}</strong>
                                </Typography>
                                {deliveryMode === 'home' ? (
                                  <>
                                    <Typography variant="body2" color="text.secondary">
                                      追蹤號碼: {result.trackingNumber}
                                      <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(result.trackingNumber || '')}
                                        title="複製追蹤號碼"
                                        sx={{ ml: 1 }}
                                      >
                                        <ContentCopy fontSize="small" />
                                      </IconButton>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      配送業者: {result.carrier}
                                    </Typography>
                                    {result.destinationCode && (
                                      <Typography variant="body2" color="text.secondary">
                                        目的地代碼: {result.destinationCode}
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {combinedCode && (
                                      <Typography variant="h6" color="primary" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 1,
                                        fontFamily: 'monospace'
                                      }}>
                                        {combinedCode}
                                        <IconButton
                                          size="small"
                                          onClick={() => copyToClipboard(combinedCode)}
                                          title="複製追蹤代碼"
                                          color="primary"
                                        >
                                          <ContentCopy fontSize="small" />
                                        </IconButton>
                                      </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                      物流編號: {result.logisticsId}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      繳費代號: {result.paymentNo}
                                    </Typography>
                                    {result.validationNo && (
                                      <Typography variant="body2" color="text.secondary">
                                        驗證碼: {result.validationNo}
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </Box>

                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Print />}
                                onClick={() => printSingleLabel(result)}
                              >
                                列印
                              </Button>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                >
                  {deliveryMode === 'home' && batchMode ? '建立新批次' :
                    deliveryMode === 'home' ? '建立新宅配訂單' :
                      batchMode ? '建立新批次' : '建立新訂單'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      <Tabs
        value={mainTab}
        onChange={(_e, v) => setMainTab(v)}
        sx={{ mb: 3 }}
        variant="fullWidth"
      >
        <Tab icon={<LocalShipping />} iconPosition="start" label="台灣物流出貨" />
        <Tab icon={<FlightTakeoff />} iconPosition="start" label="🌏 國際退貨 (順豐)" />
      </Tabs>

      {mainTab === 0 && (
        <Paper elevation={2} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
            <LocalShipping sx={{ mr: 2, verticalAlign: 'middle' }} />
            智能物流訂單系統
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}

          {activeStep < 3 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                上一步
              </Button>

              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!validateStep(activeStep)}
              >
                下一步
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {mainTab === 1 && (
        <Paper elevation={2} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
            <FlightTakeoff sx={{ mr: 2, verticalAlign: 'middle' }} />
            國際退貨標籤
          </Typography>
          <HKReturnLabelPanel />
        </Paper>
      )}
    </Box>
  );
};

export default ShippingOrderPage;